import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious"
import SkipNextIcon from "@mui/icons-material/SkipNext"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import ClearIcon from "@mui/icons-material/Clear"

/**
 * Reference frame + contact taps for a clip upload (see ALIGNMENT.md).
 *
 * The pipeline solves the climber's metric scale on ONE frame. The user
 * scrubs the video to a moment where the climber is clearly on the wall,
 * freezes it, and taps each point where they touch the wall (a hand on a
 * hold, a heel on a foothold). Taps are plain points; the pod assigns each
 * to the nearest fingertip / toe / heel of SAM's detected skeleton. No taps
 * means "assume all four limbs are on the wall".
 *
 * Emits {reference_time_s, taps} through onChange:
 *   reference_time_s  seconds into the video (null for a photo, or when the
 *                     browser cannot decode the file and no time was typed)
 *   taps              [{x, y}, ...] normalized to the displayed frame
 *                     (0..1; rotation-safe, resolution-independent)
 */

export const FRAME_STEP_S = 1 / 30

/** Normalized (0..1) point of a pointer event inside an element's box. */
export function normalizedPoint(rect, clientX, clientY) {
  if (!rect || !rect.width || !rect.height) return null
  const x = (clientX - rect.left) / rect.width
  const y = (clientY - rect.top) / rect.height
  if (x < 0 || x > 1 || y < 0 || y > 1) return null
  return { x: Math.round(x * 1e5) / 1e5, y: Math.round(y * 1e5) / 1e5 }
}

/** Toggle a tap: a tap within `radius` (normalized) of an existing one
 *  removes it, otherwise it is appended. */
export function toggleTap(taps, point, radius = 0.025) {
  const i = taps.findIndex(
    (t) => Math.hypot(t.x - point.x, t.y - point.y) < radius
  )
  if (i >= 0) return taps.filter((_, k) => k !== i)
  return [...taps, point]
}

export function formatTime(s) {
  if (s == null || Number.isNaN(s)) return "--"
  const m = Math.floor(s / 60)
  return `${m}:${(s - m * 60).toFixed(2).padStart(5, "0")}`
}

const IMAGE_EXT_RE = /\.(jpe?g|png|bmp|tiff?|webp|heic|heif)$/i
function isImage(file) {
  if (!file) return false
  if (file.type && file.type.startsWith("image/")) return true
  return IMAGE_EXT_RE.test(file.name || "")
}

/** The frozen frame with tap markers on top. */
export function TapCanvas({ src, taps, onToggle, disabled }) {
  const imgRef = useRef(null)
  const onClick = (e) => {
    if (disabled || !imgRef.current) return
    const p = normalizedPoint(
      imgRef.current.getBoundingClientRect(),
      e.clientX,
      e.clientY
    )
    if (p) onToggle(p)
  }
  return (
    <Box
      sx={{
        // Markers are positioned as a percentage of THIS box, so it must be
        // exactly the image's size: as a flex child of a column Stack it
        // would otherwise stretch to the full width while a portrait image
        // stays narrow, and every marker would drift right with x.
        position: "relative",
        display: "inline-block",
        width: "fit-content",
        alignSelf: "flex-start",
        maxWidth: "100%",
        lineHeight: 0,
        cursor: disabled ? "default" : "crosshair",
        userSelect: "none",
      }}
      onClick={onClick}
      data-testid="tap-canvas"
    >
      <img
        ref={imgRef}
        src={src}
        alt="reference frame"
        style={{ maxWidth: "100%", maxHeight: 480, display: "block" }}
        draggable={false}
      />
      {taps.map((t, i) => (
        <Box
          key={`${t.x}-${t.y}`}
          sx={{
            position: "absolute",
            left: `${t.x * 100}%`,
            top: `${t.y * 100}%`,
            transform: "translate(-50%, -50%)",
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: "2px solid #ffeb3b",
            bgcolor: "rgba(255, 235, 59, 0.35)",
            color: "#000",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: "18px",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          {i + 1}
        </Box>
      ))}
    </Box>
  )
}

export default function ReferencePicker({ file, onChange, disabled }) {
  const videoRef = useRef(null)
  const [url, setUrl] = useState(null)
  const [duration, setDuration] = useState(null)
  const [time, setTime] = useState(0)
  const [decodeError, setDecodeError] = useState(false)
  const [manualTime, setManualTime] = useState("")
  const [still, setStill] = useState(null) // data URL of the frozen frame
  const [frozenAt, setFrozenAt] = useState(null)
  const [taps, setTaps] = useState([])
  const photo = isImage(file)

  // Object URL for the chosen file; revoked when the file changes.
  useEffect(() => {
    setStill(null)
    setFrozenAt(null)
    setTaps([])
    setDecodeError(false)
    setManualTime("")
    setDuration(null)
    setTime(0)
    if (!file || typeof URL.createObjectURL !== "function") {
      // No object URLs (old browser / test env): no preview, time can still
      // be typed for a video.
      setUrl(null)
      if (file && !photo) setDecodeError(true)
      return undefined
    }
    const u = URL.createObjectURL(file)
    setUrl(u)
    if (photo) setStill(u)
    return () => URL.revokeObjectURL(u)
  }, [file, photo])

  // Report upward whenever the answer changes.
  useEffect(() => {
    if (!onChange) return
    let t = null
    if (!photo) {
      if (frozenAt != null) t = frozenAt
      else if (decodeError && manualTime !== "" && !Number.isNaN(+manualTime))
        t = Math.max(0, +manualTime)
    }
    onChange({ reference_time_s: t, taps })
  }, [frozenAt, taps, manualTime, decodeError, photo, onChange])

  const step = useCallback((delta) => {
    const v = videoRef.current
    if (!v) return
    v.pause()
    const next = Math.min(
      Math.max(0, v.currentTime + delta),
      Number.isFinite(v.duration) ? v.duration : Infinity
    )
    v.currentTime = next
    setTime(next)
  }, [])

  const freeze = () => {
    const v = videoRef.current
    if (!v || !v.videoWidth) return
    v.pause()
    const c = document.createElement("canvas")
    c.width = v.videoWidth
    c.height = v.videoHeight
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height)
    setStill(c.toDataURL("image/jpeg", 0.85))
    setFrozenAt(Math.round(v.currentTime * 1000) / 1000)
    setTaps([])
  }

  const unfreeze = () => {
    setStill(null)
    setFrozenAt(null)
    setTaps([])
  }

  if (!file) return null

  const tapHelp =
    taps.length === 0
      ? "Tap each point where the climber touches the wall (hands, feet, a heel or a thumb if that is what is on). " +
        "At least two different limbs give the best scale. No taps = all four limbs assumed on the wall."
      : `${taps.length} contact${taps.length === 1 ? "" : "s"} marked. Tap a marker to remove it.`

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Reference frame {photo ? "(the photo)" : ""}
      </Typography>

      {!photo && !still && (
        <Stack spacing={1}>
          {!decodeError ? (
            <>
              <video
                ref={videoRef}
                src={url}
                controls
                playsInline
                muted
                preload="metadata"
                style={{ maxWidth: "100%", maxHeight: 420, display: "block" }}
                onLoadedMetadata={(e) => setDuration(e.target.duration)}
                onTimeUpdate={(e) => setTime(e.target.currentTime)}
                onError={() => setDecodeError(true)}
              />
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Tooltip title="Back one frame">
                  <span>
                    <IconButton size="small" onClick={() => step(-FRAME_STEP_S)} disabled={disabled}>
                      <SkipPreviousIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Forward one frame">
                  <span>
                    <IconButton size="small" onClick={() => step(FRAME_STEP_S)} disabled={disabled}>
                      <SkipNextIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                  {formatTime(time)} / {formatTime(duration)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={freeze}
                  disabled={disabled}
                >
                  Use this frame
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Scrub to a moment where the climber is clearly on the wall and the wall is in view.
                Leave it and the pipeline picks a frame itself.
              </Typography>
            </>
          ) : (
            <>
              <Alert severity="info">
                This browser can&apos;t play this file, so there is no preview. Type the time
                (seconds) of a frame where the climber is on the wall, or leave it blank to
                let the pipeline choose.
              </Alert>
              <TextField
                size="small"
                label="Reference time (s)"
                value={manualTime}
                onChange={(e) => setManualTime(e.target.value)}
                inputProps={{ inputMode: "decimal", "aria-label": "Reference time (s)" }}
                sx={{ maxWidth: 220 }}
              />
            </>
          )}
        </Stack>
      )}

      {still && (
        <Stack spacing={1}>
          <TapCanvas src={still} taps={taps} onToggle={(p) => setTaps(toggleTap(taps, p))} disabled={disabled} />
          <Typography variant="caption" color="text.secondary">
            {tapHelp}
          </Typography>
          <Stack direction="row" spacing={1}>
            {!photo && (
              <Button size="small" startIcon={<ClearIcon />} onClick={unfreeze} disabled={disabled}>
                Pick another frame ({formatTime(frozenAt)})
              </Button>
            )}
            {taps.length > 0 && (
              <Button size="small" onClick={() => setTaps([])} disabled={disabled}>
                Clear taps
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
